import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, FileText, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Exams = () => {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '', description: '', start_date: '', end_date: '', exam_type: 'term',
  });

  useEffect(() => { fetchExams(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchExams();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchExams = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get('/exams', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      setExams(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch exams', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      await api.post('/exams', {
        ...formData,
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      toast({ title: 'Success', description: 'Exam created successfully' });
      setIsDialogOpen(false);
      setFormData({ name: '', description: '', start_date: '', end_date: '', exam_type: 'term' });
      fetchExams();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create exam', variant: 'destructive' });
    }
  };

  const examTypes = [
    { value: 'term', label: 'Term Exam' },
    { value: 'mid_term', label: 'Mid-Term' },
    { value: 'final', label: 'Final Exam' },
    { value: 'unit_test', label: 'Unit Test' },
    { value: 'other', label: 'Other' },
  ];

  const statusColors = {
    scheduled: 'bg-blue-500/20 text-blue-400',
    ongoing: 'bg-amber-500/20 text-amber-400',
    completed: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="space-y-6" data-testid="exams-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Examinations</h1>
          <p className="text-slate-400">Manage exams and schedules</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Create Exam
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader><DialogTitle>Create New Exam</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Exam Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-700 border-slate-600" required />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Exam Type</Label>
                <Select value={formData.exam_type} onValueChange={(v) => setFormData({...formData, exam_type: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {examTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-slate-700 border-slate-600" required />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="bg-slate-700 border-slate-600" required />
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Create Exam</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : exams.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No exams found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {exams.map(exam => (
            <Card key={exam.id} className="bg-slate-800 border-slate-700 hover:border-slate-600">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex flex-col items-center justify-center">
                      <span className="text-xs text-blue-400">{new Date(exam.start_date).toLocaleDateString('en', { month: 'short' })}</span>
                      <span className="text-lg font-bold text-white">{new Date(exam.start_date).getDate()}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-lg">{exam.name}</h3>
                      <p className="text-sm text-slate-400">{exam.description || examTypes.find(t => t.value === exam.exam_type)?.label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-3 h-3 text-slate-500" />
                        <span className="text-xs text-slate-500">
                          {new Date(exam.start_date).toLocaleDateString()} - {new Date(exam.end_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${statusColors[exam.status] || statusColors.scheduled}`}>
                    {exam.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Exams;
